import { ArrowRight, Compass, Lightbulb, Lock, Target, TrendingDown } from 'lucide-react'
import { Button } from './ui'

const CTA_URL = import.meta.env.VITE_CTA_URL || '#'

const GRAVIDADE = {
  critico: { label: 'Crítico', cor: 'var(--danger)', bg: 'bg-danger/10', text: 'text-danger' },
  significativo: {
    label: 'Significativo',
    cor: 'var(--warning)',
    bg: 'bg-warning/10',
    text: 'text-warning',
  },
  leve: { label: 'Leve', cor: 'var(--success)', bg: 'bg-success/10', text: 'text-success' },
}

// Resultado v2: nomeia o gargalo de escala (arquetipo) + gravidade + parecer da IA.
export function Report({ resultado }) {
  const r = resultado
  const a = r.analise || null
  const planoIA = new Map((a?.plano || []).map((p) => [p.codigo, p]))
  const grav = GRAVIDADE[r.gravidade_nivel]
  const saudavel = r.arquetipo === 'saudavel'

  return (
    <div className="w-full space-y-6">
      {/* Cabecalho do gargalo */}
      <div className="animate-scale-in text-center">
        <div className="mb-3 text-5xl">{r.emoji}</div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {saudavel ? 'Diagnóstico' : 'Seu gargalo de escala'}
        </p>
        <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight md:text-4xl">
          {r.rotulo}
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">
          {a?.espelho || r.manchete}
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {!saudavel && grav && (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold ${grav.bg} ${grav.text}`}
            >
              Gravidade: {grav.label}
            </span>
          )}
          {r.secundario && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-1.5 text-xs font-medium text-muted-foreground">
              Também tem sinal de: {r.secundario.rotulo}
            </span>
          )}
        </div>
      </div>

      {/* Diagnostico (IA) */}
      {a?.diagnostico?.length > 0 && (
        <div className="glass rounded-3xl p-6 md:p-8">
          <h2 className="mb-5 font-display text-lg font-bold">O que está travando a sua escala</h2>
          <div className="space-y-5">
            {a.diagnostico.map((d, i) => (
              <div key={i} className="border-l-2 border-primary/30 pl-4">
                <h3 className="font-semibold text-foreground">{d.titulo}</h3>
                <dl className="mt-2 space-y-1.5 text-sm leading-relaxed">
                  <Linha rotulo="Sintoma" texto={d.sintoma} />
                  <Linha rotulo="Causa" texto={d.causa} />
                  <Linha rotulo="Efeito" texto={d.efeito} />
                </dl>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custo da inacao (IA) */}
      {a?.custo_inacao && (
        <div className="glass rounded-3xl border-l-4 border-l-warning/60 p-6 md:p-8">
          <div className="mb-1 flex items-center gap-2">
            <TrendingDown className="h-5 w-5 text-warning" />
            <h2 className="font-display text-lg font-bold">O que isso está custando</h2>
          </div>
          <p className="text-base leading-relaxed text-muted-foreground">{a.custo_inacao}</p>
        </div>
      )}

      {/* Destino (IA) */}
      {a?.destino && (
        <div className="glass rounded-3xl p-6 md:p-8">
          <div className="mb-1 flex items-center gap-2">
            <Compass className="h-5 w-5 text-success" />
            <h2 className="font-display text-lg font-bold">Como seria com isso resolvido</h2>
          </div>
          <p className="text-base leading-relaxed text-muted-foreground">{a.destino}</p>
        </div>
      )}

      {/* Plano (do arquetipo + IA) */}
      {r.plano?.length > 0 && (
        <div className="glass rounded-3xl p-6 md:p-8">
          <div className="mb-1 flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            <h2 className="font-display text-lg font-bold">Seu plano pra destravar</h2>
          </div>
          <p className="mb-5 text-sm text-muted-foreground">
            Focado no seu gargalo. Comece de cima pra baixo.
          </p>
          <ol className="stagger-children space-y-3">
            {r.plano.map((acao, idx) => {
              const enr = planoIA.get(acao.codigo)
              return (
                <li key={acao.codigo} className="glass-subtle flex gap-4 rounded-2xl p-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary font-display text-sm font-bold text-primary-foreground">
                    {idx + 1}
                  </span>
                  <div>
                    <h3 className="font-semibold text-foreground">{acao.titulo}</h3>
                    <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">
                      {enr?.porque || acao.descricao}
                    </p>
                    {enr?.indicador && (
                      <p className="mt-1.5 text-xs font-medium text-text-tertiary">
                        Mede por: {enr.indicador}
                      </p>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
        </div>
      )}

      {/* Lacuna (IA) */}
      {a?.lacuna && (
        <div className="glass-strong rounded-3xl p-6 md:p-8">
          <div className="mb-1 flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" />
            <h2 className="font-display text-lg font-bold">Por que isso não se resolve sozinho</h2>
          </div>
          <p className="text-base leading-relaxed text-foreground">{a.lacuna}</p>
        </div>
      )}

      {/* Quick win (IA) */}
      {a?.quick_win?.descricao && (
        <div className="glass rounded-3xl p-6 md:p-8">
          <div className="mb-1 flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-secondary" />
            <h2 className="font-display text-lg font-bold">
              {a.quick_win.titulo || 'Comece por aqui essa semana'}
            </h2>
          </div>
          <p className="text-base leading-relaxed text-muted-foreground">{a.quick_win.descricao}</p>
        </div>
      )}

      {/* CTA */}
      <div className="glass-strong rounded-3xl p-6 text-center md:p-8">
        {a?.fechamento && (
          <p className="mx-auto mb-3 max-w-lg text-base leading-relaxed text-muted-foreground">
            {a.fechamento}
          </p>
        )}
        <p className="mx-auto max-w-lg text-base font-medium leading-relaxed text-foreground">
          {r.cta}
        </p>
        <div className="mt-5 flex justify-center">
          <a href={CTA_URL} target="_blank" rel="noreferrer">
            <Button size="lg" className="animate-soft-pulse">
              Quero conversar
              <ArrowRight className="h-4 w-4" />
            </Button>
          </a>
        </div>
      </div>

      <p className="pb-4 text-center text-xs text-text-tertiary">Infosaas · Studio</p>
    </div>
  )
}

function Linha({ rotulo, texto }) {
  if (!texto) return null
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 font-semibold text-text-tertiary">{rotulo}:</dt>
      <dd className="text-muted-foreground">{texto}</dd>
    </div>
  )
}
